import { zodResolver } from "@hookform/resolvers/zod";
import {
  LoginErrorState,
  LoginView,
} from "dsl/ecosystems/CareproviderOnboardingLoginPage/useHandleLogin";
import { useCallback, useEffect, useRef } from "react";
import {
  DefaultValues,
  Path,
  useForm,
  UseFormReturn,
  UseFormWatch,
} from "react-hook-form";
import { useTranslations } from "translations";
import Translations from "translations/types";
import { getLoginDefaultValues } from "./getLoginDefaultValues";
import {
  getLoginSchema,
  LoginFormValues,
  LoginParsedValues,
} from "./loginSchema";

const LOGIN_FIELD_NAMES = ["email", "password", "challenge"] as const;

type LoginFieldName = (typeof LOGIN_FIELD_NAMES)[number];

function isLoginFieldName(name: string | undefined): name is LoginFieldName {
  return name === "email" || name === "password" || name === "challenge";
}

type ResolverCtx = {
  isSingleSignOn: boolean;
  translations: Translations;
  view: LoginView;
};

type UseLoginFormParams = {
  isSingleSignOn?: boolean;
  loginErrors?: LoginErrorState;
  loginView: LoginView;
  onBackToLogin: () => void;
  onChangeEmail?: (value: string) => void;
  onSubmit: (payload: LoginParsedValues) => void;
  resetError: () => void;
  ssoToken?: string;
};

function useSyncLoginExternalErrors({
  loginErrors,
  methods,
}: {
  loginErrors?: LoginErrorState;
  methods: UseFormReturn<LoginFormValues, unknown, LoginParsedValues>;
}) {
  useEffect(() => {
    LOGIN_FIELD_NAMES.forEach((field) => {
      const fieldName = field as Path<LoginFormValues>;
      const externalMessage = loginErrors?.[field];
      if (externalMessage) {
        methods.setError(fieldName, { message: externalMessage });
      } else if (methods.getFieldState(fieldName).error?.type === "manual") {
        methods.clearErrors(fieldName);
      }
    });
  }, [loginErrors, methods]);
}

function useLoginFieldWatchSideEffects({
  onChangeEmail,
  resetError,
  watch,
}: {
  onChangeEmail?: (value: string) => void;
  resetError: () => void;
  watch: UseFormWatch<LoginFormValues>;
}) {
  const onChangeEmailRef = useRef(onChangeEmail);
  const resetErrorRef = useRef(resetError);
  onChangeEmailRef.current = onChangeEmail;
  resetErrorRef.current = resetError;

  useEffect(() => {
    const subscription = watch((values, { name, type }) => {
      if (type !== "change" || !isLoginFieldName(name)) {
        return;
      }

      resetErrorRef.current();

      if (name === "email") {
        onChangeEmailRef.current?.(values.email ?? "");
      }
    });

    return () => subscription.unsubscribe();
  }, [watch]);
}

export function useLoginForm({
  isSingleSignOn = false,
  loginErrors,
  loginView,
  onBackToLogin,
  onChangeEmail,
  onSubmit,
  resetError,
  ssoToken,
}: UseLoginFormParams) {
  const translations = useTranslations();

  const resolverCtxRef = useRef<ResolverCtx>({
    isSingleSignOn,
    translations,
    view: loginView,
  });
  resolverCtxRef.current = {
    isSingleSignOn,
    translations,
    view: loginView,
  };

  const getResetValues = useCallback(
    (): DefaultValues<LoginFormValues> => getLoginDefaultValues({ ssoToken }),
    [ssoToken],
  );

  const methods = useForm<LoginFormValues, unknown, LoginParsedValues>({
    defaultValues: getLoginDefaultValues({ ssoToken }),
    resolver: async (values, context, options) => {
      const schema = getLoginSchema(resolverCtxRef.current);
      return zodResolver(schema)(values, context, options);
    },
  });

  useEffect(() => {
    methods.setValue("ssoToken", ssoToken, { shouldDirty: false });
  }, [methods, ssoToken]);

  useLoginFieldWatchSideEffects({
    onChangeEmail,
    resetError,
    watch: methods.watch,
  });

  useSyncLoginExternalErrors({ loginErrors, methods });

  const handleValid = useCallback(
    (values: LoginParsedValues) => {
      onSubmit(values);
    },
    [onSubmit],
  );

  const handleBackToLogin = useCallback(() => {
    methods.reset(getResetValues());
    onBackToLogin();
  }, [getResetValues, methods, onBackToLogin]);

  return {
    handleBackToLogin,
    handleSubmit: methods.handleSubmit(handleValid),
    methods,
  };
}
